/**
 * TaxiPathRenderer — draws a faint ground corridor under each currently-moving
 * actor's active taxi/tow leg. Lifecycle is keyed by leg so each static path is
 * built once (when the leg becomes active) and removed when it ends — no
 * per-frame geometry churn. Airborne legs are skipped (no ground ribbon).
 *
 * Inspired by the autostacker's SweepPathRenderer, trimmed to a single ribbon.
 */
import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import { samplePolyline, type Pt } from '../motion/easing'

const RIBBON_Y = 0.4 * FEET_TO_METERS // just above the apron
const HALF_WIDTH_M = 4
const SAMPLES = 24

export interface ActiveRibbon {
  key: string
  path: Pt[]
  kind: 'taxi' | 'tow' | 'tug_empty'
}

const COLOR: Record<ActiveRibbon['kind'], number> = {
  taxi: 0x38bdf8, // sky
  tow: 0xf2b500, // amber (matches tug)
  tug_empty: 0x64748b, // slate (empty repositioning)
}

export class TaxiPathRenderer {
  private group = new THREE.Group()
  private meshes = new Map<string, THREE.Mesh>()
  private mats = new Map<ActiveRibbon['kind'], THREE.MeshBasicMaterial>()

  constructor(private scene: THREE.Scene) {
    this.group.name = 'evt-taxi-paths'
    scene.add(this.group)
  }

  private matFor(kind: ActiveRibbon['kind']): THREE.MeshBasicMaterial {
    let m = this.mats.get(kind)
    if (!m) {
      m = new THREE.MeshBasicMaterial({ color: COLOR[kind], transparent: true, opacity: 0.28, side: THREE.DoubleSide, depthWrite: false })
      this.mats.set(kind, m)
    }
    return m
  }

  private buildRibbon(path: Pt[], kind: ActiveRibbon['kind']): THREE.Mesh {
    const verts: number[] = []
    let prev: THREE.Vector3 | null = null
    const pts: THREE.Vector3[] = []
    for (let i = 0; i <= SAMPLES; i++) {
      const { position } = samplePolyline(path, i / SAMPLES)
      pts.push(new THREE.Vector3(position[0] * FEET_TO_METERS, RIBBON_Y, position[1] * FEET_TO_METERS))
    }
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]
      const next = pts[i + 1] ?? p
      const dir = next.clone().sub(prev ?? p).normalize()
      const perp = new THREE.Vector3(-dir.z, 0, dir.x).multiplyScalar(HALF_WIDTH_M)
      verts.push(p.x + perp.x, p.y, p.z + perp.z, p.x - perp.x, p.y, p.z - perp.z)
      prev = p
    }
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3))
    const idx: number[] = []
    for (let i = 0; i < pts.length - 1; i++) {
      const a = i * 2
      idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2)
    }
    geo.setIndex(idx)
    return new THREE.Mesh(geo, this.matFor(kind))
  }

  /** Reconcile the drawn ribbons against the set of active legs. */
  sync(active: ActiveRibbon[]): void {
    const wanted = new Set(active.map((a) => a.key))
    for (const [key, mesh] of Array.from(this.meshes.entries())) {
      if (!wanted.has(key)) {
        this.group.remove(mesh)
        mesh.geometry.dispose()
        this.meshes.delete(key)
      }
    }
    for (const a of active) {
      if (this.meshes.has(a.key)) continue
      const mesh = this.buildRibbon(a.path, a.kind)
      this.meshes.set(a.key, mesh)
      this.group.add(mesh)
    }
  }

  dispose(): void {
    for (const mesh of this.meshes.values()) {
      this.group.remove(mesh)
      mesh.geometry.dispose()
    }
    this.meshes.clear()
    for (const m of this.mats.values()) m.dispose()
    this.mats.clear()
    this.scene.remove(this.group)
  }
}
